import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ZoomableGroup, ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { useNavigate } from 'react-router-dom'; // Import useNavigate hook
import { useParams } from 'react-router-dom'; // Import useParams
import { Helmet } from 'react-helmet';
import Lottie from 'lottie-react';
import './TravelTopics.css';
import sightseeingAnim from './animations/travel-planning.json';
import foodAnim from './animations/restaurant.json';
import statsAnim from './animations/presentator.json';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const geoJson = '/assets/maps/world-50m.json';

interface Topic {
  name: 'Sightseeing' | 'Food' | 'Statistics';
  icon: string;
  animation: object;
}

interface CountryResult {
  name: string;
  iso: string;
}

interface GeoFeature {
  rsmKey: string;
  properties: {
    name: string;
  };
}

interface TravelTopicsProps {
  onTopicPathChange: (path: string) => void;
}

const topicsArray: Topic[] = [
  { name: 'Sightseeing', icon: 'hiking', animation: sightseeingAnim },
  { name: 'Food', icon: 'restaurant_menu', animation: foodAnim },
  { name: 'Statistics', icon: 'attach_money', animation: statsAnim },
];

const canonicalUrl = 'https://livingoutofabackpack.com/traveltopics/';

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://livingoutofabackpack.com/home/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Travel Topics',
      item: canonicalUrl,
    },
  ],
};

const collectionPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Travel Topics by Country',
  description: 'Browse country travel content for sightseeing, food, and travel statistics.',
  url: canonicalUrl,
  hasPart: topicsArray.map((topic) => ({
    '@type': 'Thing',
    name: topic.name,
  })),
};

function TravelTopics({ onTopicPathChange }: TravelTopicsProps): JSX.Element {
  const { topicPath } = useParams<{ topicPath?: string }>();
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false); // Control popup visibility
  const navigate = useNavigate(); // Get navigate function from useNavigate
  const [searchQuery, setSearchQuery] = useState(''); // State to store the search input
  const [searchResults, setSearchResults] = useState<CountryResult[]>([]);

  // Extract country and topic from the topicPath URL
  useEffect(() => {
    if (topicPath) {
      const matchedTopic = topicsArray.find((topic) => topic.name.toLowerCase() === topicPath.toLowerCase());
      setSelectedTopic(matchedTopic ?? null);
      onTopicPathChange(topicPath); // Pass the topic path back to parent component
    } else {
      setSelectedTopic(null); // Clear the selected topic if no valid topicPath
    }
  }, [topicPath, onTopicPathChange]);

  const handleButtonClick = (topic: Topic): void => {
    setSelectedTopic(topic); // Store the clicked topic
    setIsPopupOpen(true); // Open the popup
  };

  const closePopup = (): void => {
    setIsPopupOpen(false); // Close the popup
    setSelectedTopic(null); // Clear the selected topic
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    // Close the popup only if the click is outside the popup content
    if (e.target === e.currentTarget) {
      closePopup();
    }
  };

  // Function to fetch country details
  const handleClick = (geo: GeoFeature, topic: Topic | null): void => {
    if (!topic) {
      return;
    }

    const countryName = geo.properties.name; // Adjust based on actual field
    const topicPath = `/${countryName}/topics/${topic.name.toLowerCase()}`;

    navigate(`/traveltopics${topicPath}`); // Navigate to the new route

    if (!countryName) {
      console.error('Country name is undefined!');
      return;
    }
    onTopicPathChange(topicPath); // Update the state in App.js
  };

  // Function to handle the search query change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const query = event.target.value;
    setSearchQuery(query);

    // If search query is not empty, make an API call to search countries by name
    if (query.length > 0) {
      axios
        .get(`${backendUrl}/worlddata/countries?name=${query}`)
        .then((response: { data: CountryResult[] }) => {
          setSearchResults(response.data); // Update the search results with matching countries
        })
        .catch((error) => console.error('Error searching countries', error));
    } else {
      setSearchResults([]); // Clear search results when search query is empty
    }
  };

  return (
    <div className="TravelTopicsPage">
      <Helmet>
        <title>Travel Topics by Country | Living Out Of A Backpack</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="description" content="Choose a country and explore dedicated travel topics for sightseeing, food, and statistics in one place." />
        <meta property="og:title" content="Travel Topics by Country | Living Out Of A Backpack" />
        <meta property="og:description" content="Pick a country and jump into curated sightseeing ideas, local food highlights, and travel statistics." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://livingoutofabackpack.com/static/logo.jpg" />
        <meta property="og:image:alt" content="Travel Topics by Country" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(collectionPageJsonLd)}</script>
      </Helmet>
      <div className="grid-background">
        <div className="grid-container">
          {topicsArray.map((topic) => (
            <div key={topic.name} className="grid-item">
              <button
                className="button-content"
                onClick={() => handleButtonClick(topic)} // Handle click event
              >
                <div className="picture-content">
                  <Lottie animationData={topic.animation} loop={true} />
                </div>
                <span className="material-icons icon">{topic.icon}</span>
                <div className="block-title">{topic.name}</div>
                <div className="block-text">Explore {topic.name.toLowerCase()} options</div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Popup Component */}
      {isPopupOpen && (
        <div
          className="popup-overlay"
          onClick={handleOverlayClick} // Handle outside clicks
        >
          <div className="popup-content">
            <div className="popup-header">
              <div className="OverlayTitle">
                {selectedTopic?.name}
                <span className="material-icons icon">{selectedTopic?.icon}</span>
              </div>

              <button className="OverlayCloseButton" onClick={closePopup}>
                &times;
              </button>
            </div>

            <p>
              <strong> Pick a country to proceed to the {selectedTopic?.name} topic.</strong>
            </p>

            <div className="SearchBarContainer">
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  background: 'var(--surface-elevated)',
                  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                  borderRadius: '4px',
                  zIndex: '1000',
                }}
              >
                <input className="SearchBarInput" type="text" placeholder="Search for a country" value={searchQuery} onChange={handleSearchChange} />
                {searchQuery.length > 0 && (
                  <div className="SearchBarResults">
                    <ul className="SearchBarResultsList">
                      {searchResults.length > 0 ? (
                        searchResults.map((country) => (
                          <li className="SearchBarResultsItem" key={country.iso} onClick={() => navigate(`/traveltopics/${country.name}/topics/${selectedTopic?.name}`)}>
                            {country.name}
                          </li>
                        ))
                      ) : (
                        <li style={{ textAlign: 'center', padding: '8px', color: 'var(--text-color)' }}>No matching countries found</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <ComposableMap>
              <ZoomableGroup
                center={[-80, 25]}
                zoom={6} // Zoom in on mobile
                minZoom={1}
                maxZoom={6}
                translateExtent={[
                  [0, -50], // Minimum X (-1000), Minimum Y (-500)
                  [900, 700], // Maximum X (1000), Maximum Y (500)
                ]} // Restrict panning
              >
                <Geographies geography={geoJson}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onClick={() => handleClick(geo, selectedTopic)}
                        style={{
                          default: {
                            fill: '#4CAF50',
                            outline: 'none',
                            stroke: '#FFFFFF',
                            strokeWidth: 0.2,
                          },
                          hover: {
                            fill: '#ffc658',
                            outline: 'none', // Remove outline
                            stroke: '#FFFFFF', // Add a white stroke to outline the country
                            strokeWidth: 0.2, // Set the width of the stroke (outline)
                            zIndex: 1000,
                          },
                          pressed: {
                            fill: '#e6a02f',
                            outline: 'none',
                            stroke: '#FFFFFF', // Add a white stroke when pressed
                            strokeWidth: 0.2, // Set the width of the pressed stroke
                          },
                        }}
                      />
                    ))
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
          </div>
        </div>
      )}
    </div>
  );
}

export default TravelTopics;
